// =============================================================================
// ArReceiptModal — T-346. Record ONE bank credit from a client and allocate it
// across their open invoices (full or partial). Solves the HVAC lump-payment
// problem: $54,760.70 lands with no remittance → record once, auto-spread
// oldest-first (editable), invoices flip paid / part_paid via mig 225 trigger.
// =============================================================================

import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, Wand2, CheckCircle2 } from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Field, Input, Spinner } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

export default function ArReceiptModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const [clientId, setClientId] = useState("");
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [bankRef, setBankRef] = useState("");
  const [remittanceFile, setRemittanceFile] = useState<File | null>(null);   // T-442 — client's inbound remittance PDF
  const [alloc, setAlloc] = useState<Record<string, string>>({});   // invoice_id -> $ string
  const [saving, setSaving] = useState(false);

  const { data: clients = [] } = useQuery({
    queryKey: ["ar_receipt_clients"],
    queryFn: async () => {
      const { data } = await supabase.from("clients")
        .select("client_id, client_code, legal_name, trading_name").order("legal_name");
      return (data ?? []) as any[];
    },
  });

  // Open (payable) invoices for the picked client, oldest first.
  const { data: invoices = [], isLoading: invLoading } = useQuery({
    queryKey: ["ar_receipt_open_invoices", clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data } = await supabase.from("client_invoices")
        .select("invoice_id, invoice_number, invoice_date, total_incl_gst, amount_paid, status, projects:project_id(project_code)")
        .eq("client_id", clientId)
        .in("status", ["sent", "overdue", "part_paid", "approved"])
        .order("invoice_date", { ascending: true });
      return (data ?? []) as any[];
    },
  });

  const receiptAmt = Number(amount) || 0;
  const allocated = useMemo(
    () => Object.values(alloc).reduce((a, v) => a + (Number(v) || 0), 0),
    [alloc]);
  const remaining = Math.round((receiptAmt - allocated) * 100) / 100;

  function outstanding(inv: any): number {
    return Math.max(0, Math.round((Number(inv.total_incl_gst) - Number(inv.amount_paid ?? 0)) * 100) / 100);
  }

  // Auto-allocate oldest-first until the receipt is spent.
  function autoAllocate() {
    let left = receiptAmt;
    const next: Record<string, string> = {};
    for (const inv of invoices) {
      if (left <= 0) break;
      const out = outstanding(inv);
      if (out <= 0) continue;
      const take = Math.min(out, left);
      next[inv.invoice_id] = take.toFixed(2);
      left = Math.round((left - take) * 100) / 100;
    }
    setAlloc(next);
  }

  async function save() {
    if (!clientId) { window.alert("Pick the client."); return; }
    if (receiptAmt <= 0) { window.alert("Enter the bank credit amount."); return; }
    const rows = Object.entries(alloc)
      .map(([invoice_id, v]) => ({ invoice_id, amount: Math.round((Number(v) || 0) * 100) / 100 }))
      .filter(r => r.amount > 0);
    if (rows.length === 0) { window.alert("Allocate the money to at least one invoice (Auto-allocate does it oldest-first)."); return; }
    if (allocated - receiptAmt > 0.005) { window.alert(`Allocated ${fmtMoney(allocated)} is more than the receipt ${fmtMoney(receiptAmt)}.`); return; }
    for (const r of rows) {
      const inv = invoices.find(i => i.invoice_id === r.invoice_id);
      if (inv && r.amount - outstanding(inv) > 0.005) {
        window.alert(`${inv.invoice_number}: allocation ${fmtMoney(r.amount)} exceeds its outstanding ${fmtMoney(outstanding(inv))}.`);
        return;
      }
    }
    setSaving(true);
    try {
      const { data: rec, error } = await supabase.from("ar_receipts").insert({
        client_id: clientId,
        received_date: receivedDate,
        amount: receiptAmt,
        bank_reference: bankRef.trim() || null,
        created_by: (user as any)?.user_id ?? null,
      }).select("receipt_id").single();
      if (error) throw error;
      const { error: aErr } = await supabase.from("ar_receipt_allocations")
        .insert(rows.map(r => ({ receipt_id: (rec as any).receipt_id, ...r })));
      if (aErr) throw aErr;

      // T-442 — attach the client's inbound remittance PDF (same R2 + files
      // pattern as invoice PDFs). Non-fatal: the payment is already recorded,
      // so a failed upload only warns rather than losing the receipt.
      if (remittanceFile) {
        try {
          const objectKey = `ar_receipts/${(rec as any).receipt_id}/${remittanceFile.name}`;
          const signRes = await apiFetch("/sign-upload", {
            method: "POST",
            body: JSON.stringify({
              bucket:             "coretel-prod",
              object_key:         objectKey,
              content_type:       remittanceFile.type || "application/pdf",
              expires_in_seconds: 900,
            }),
          });
          if (!signRes.ok) throw new Error(`sign-upload failed (${signRes.status})`);
          const { upload_url } = await signRes.json();
          const putRes = await fetch(upload_url, {
            method:  "PUT",
            headers: { "Content-Type": remittanceFile.type || "application/pdf" },
            body:    remittanceFile,
          });
          if (!putRes.ok) throw new Error(`R2 PUT failed (${putRes.status})`);
          const { error: fErr } = await supabase.from("files").insert({
            r2_bucket:       "coretel-prod",
            r2_object_key:   objectKey,
            file_name:       remittanceFile.name,
            file_size_bytes: remittanceFile.size,
            content_type:    remittanceFile.type || "application/pdf",
            parent_table:    "ar_receipts",
            parent_id:       (rec as any).receipt_id,
          });
          if (fErr) throw fErr;
        } catch (upErr: any) {
          window.alert(`Payment saved, but the remittance file didn't attach: ${upErr.message ?? upErr}. You can add it again later.`);
        }
      }

      qc.invalidateQueries({ queryKey: ["invoices_list"] });
      qc.invalidateQueries({ queryKey: ["invoices_kpi"] });
      onSaved();
    } catch (e: any) {
      window.alert(`Couldn't record the payment: ${e.message ?? e}`);
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line">
          <h3 className="text-lg font-semibold flex items-center gap-2"><Banknote size={18} className="text-accent" /> Record client payment</h3>
          <p className="text-[12px] text-text-muted mt-1">
            One bank credit → spread across open invoices. Partial amounts fine — invoices flip to part-paid / paid automatically.
          </p>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Client">
              <select value={clientId} onChange={e => { setClientId(e.target.value); setAlloc({}); }}
                className="w-full bg-ink border border-line rounded px-2 py-2 text-[13px] text-text">
                <option value="">— pick —</option>
                {clients.map((c: any) => <option key={c.client_id} value={c.client_id}>{c.trading_name || c.legal_name}</option>)}
              </select>
            </Field>
            <Field label="Received"><Input type="date" value={receivedDate} onChange={e => setReceivedDate(e.target.value)} /></Field>
            <Field label="Amount (inc GST)"><Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="54760.70" /></Field>
            {/* T-410 — for RCTI clients this is the remittance doc # (Ventia "Advice of Payment") */}
            <Field label="Bank / remittance ref" hint=""><Input value={bankRef} onChange={e => setBankRef(e.target.value)} placeholder="e.g. NAB ref or remittance 2001463693" /></Field>
          </div>

          {/* T-442 — attach the client's inbound remittance / advice PDF against
              this payment. Optional; non-fatal if the upload hiccups. */}
          <div>
            <label className="block text-[11px] text-text-muted mb-1">Remittance / advice document (optional — PDF or photo)</label>
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={e => setRemittanceFile(e.target.files?.[0] ?? null)}
              className="block w-full text-[12px] text-text file:mr-3 file:rounded file:border-0 file:bg-accent file:text-white file:px-3 file:py-1.5 file:text-[12px] file:cursor-pointer"
            />
            {remittanceFile && <div className="text-[11px] text-text-muted mt-1">{remittanceFile.name} · {(remittanceFile.size / 1024).toFixed(0)} KB</div>}
          </div>

          {clientId && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Open invoices — oldest first</div>
                <Button size="sm" variant="secondary" icon={Wand2} disabled={receiptAmt <= 0 || invoices.length === 0} onClick={autoAllocate}>
                  Auto-allocate
                </Button>
              </div>
              {invLoading ? (
                <div className="py-6 flex justify-center"><Spinner size={16} /></div>
              ) : invoices.length === 0 ? (
                <div className="text-[12px] text-text-muted">No open invoices for this client.</div>
              ) : (
                <div className="space-y-1">
                  {invoices.map((inv: any) => {
                    const out = outstanding(inv);
                    return (
                      <div key={inv.invoice_id} className="flex items-center gap-3 py-1.5 border-b border-line last:border-b-0 text-[12px] flex-wrap">
                        <span className="font-mono text-accent">{inv.invoice_number}</span>
                        <span className="text-text-muted">{fmtDate(inv.invoice_date)}</span>
                        {inv.projects?.project_code && <span className="font-mono text-text-muted">{inv.projects.project_code}</span>}
                        {inv.status === "part_paid" && <span className="text-warn text-[10px]">part-paid</span>}
                        <span className="ml-auto font-mono text-text-muted">owing {fmtMoney(out)}</span>
                        <input
                          type="number" step="0.01" min="0"
                          value={alloc[inv.invoice_id] ?? ""}
                          onChange={e => setAlloc(a => ({ ...a, [inv.invoice_id]: e.target.value }))}
                          placeholder="0.00"
                          className="w-28 bg-ink border border-line rounded px-2 py-1 text-[12px] text-text text-right"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className={`text-[12px] mt-2 text-right font-mono ${Math.abs(remaining) < 0.01 ? "text-ok" : remaining < 0 ? "text-danger" : "text-warn"}`}>
                allocated {fmtMoney(allocated)} of {fmtMoney(receiptAmt)}
                {Math.abs(remaining) >= 0.01 && ` · ${remaining > 0 ? `${fmtMoney(remaining)} unallocated` : `over by ${fmtMoney(-remaining)}`}`}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button icon={CheckCircle2} disabled={saving} onClick={save}>{saving ? "Saving…" : "Record payment"}</Button>
        </div>
      </div>
    </div>
  );
}
